// frontend/src/scan/AskScan.js
// Ask Timbo bar at the top of the Scan tab. Two jobs in one input:
//   - a creator name + Enter (or a creator suggestion) scopes the grid to that creator;
//   - anything else is a question, answered by /api/ask/scan from the hov_* tables.
// All computation happens in the backend: this is input, suggestions and rendering only.
// The bar and its suggestion list use the shared .tb-ask-* classes (shared/layout.css), identical to
// perf/AskTimbo.js. AskScan.css only styles the answer card.
import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import axios from 'axios';
import { canonKey } from '../creators';
import '../shared/layout.css';
import './AskScan.css';

const API = process.env.REACT_APP_API_URL || '';

const PLACEHOLDERS = [
  'Search a creator, or ask Timbo…',
  'Who stopped uploading?',
  'Who uploads the most since September?',
  'How is Alejandra doing?',
  'What is missing a feature?',
];

// Prompt starters shown when the bar is empty.
const STARTERS = [
  { t: 'Who uploads the most?', h: 'ranking' },
  { t: 'Who stopped uploading?', h: 'quiet creators' },
  { t: 'What is missing a feature?', h: 'untagged' },
  { t: 'What is left to process, oldest first?', h: 'backlog' },
  { t: 'How many videos do we receive per week?', h: 'volume' },
];

const INTENTS = [
  { re: /stop|quiet|silent|inactive|sent|arr/, t: 'Who stopped uploading?', h: 'quiet creators' },
  { re: /most|top|rank|who|best/, t: 'Who uploads the most?', h: 'ranking' },
  { re: /feat|tag|miss/, t: 'What is missing a feature?', h: 'untagged' },
  { re: /left|pend|process|old|backlog|queue/, t: 'What is left to process, oldest first?', h: 'backlog' },
  { re: /week|many|volume|pace|receiv/, t: 'How many videos do we receive per week?', h: 'volume' },
];

function BrainIcon({ size = 25 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M9.5 3.5A2.5 2.5 0 0 0 7 6a2.5 2.5 0 0 0-2 4 2.5 2.5 0 0 0 .5 4.5A2.5 2.5 0 0 0 8 19a2.5 2.5 0 0 0 4 1.2V4.7a2.5 2.5 0 0 0-2.5-1.2Z" />
      <path d="M14.5 3.5A2.5 2.5 0 0 1 17 6a2.5 2.5 0 0 1 2 4 2.5 2.5 0 0 1-.5 4.5A2.5 2.5 0 0 1 16 19a2.5 2.5 0 0 1-4 1.2V4.7a2.5 2.5 0 0 1 2.5-1.2Z" />
      <path d="M9 8.5h1M14 8.5h1M9.5 12.5h1M13.5 12.5h1M10 16.5h1M13 16.5h1" />
    </svg>
  );
}

// Only <b>…</b> is rendered, everything else is escaped.
function RichText({ text }) {
  const parts = String(text || '').split(/(<b>.*?<\/b>)/g);
  return (
    <>
      {parts.map((p, i) => {
        const m = p.match(/^<b>(.*?)<\/b>$/);
        return m ? <b key={i}>{m[1]}</b> : <React.Fragment key={i}>{p}</React.Fragment>;
      })}
    </>
  );
}

function apiError(e) {
  const d = e && e.response && e.response.data;
  return (d && (d.message || d.details || d.error)) || (e && e.message) || 'Request failed';
}

// creators: rows from /api/hov/creators ({ creator_key, display_name, status, total, last_upload }).
// onScope(creatorRow) scopes the grid; onSearch(text) runs a filename / keyword search.
export default function AskScan({ creators, onScope, onSearch }) {
  const [q, setQ] = useState('');
  const [focus, setFocus] = useState(false);
  const [sel, setSel] = useState(-1);
  const [phIdx, setPhIdx] = useState(0);
  const [busy, setBusy] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [res, setRes] = useState(null);
  const [err, setErr] = useState(null);
  const [lastQ, setLastQ] = useState('');
  const boxRef = useRef(null);
  const inputRef = useRef(null);

  const list = useMemo(() => (creators || []).filter((c) => c.status !== 'not_creator'), [creators]);

  useEffect(() => {
    if (q) return undefined;
    const t = setInterval(() => setPhIdx((i) => (i + 1) % PLACEHOLDERS.length), 3400);
    return () => clearInterval(t);
  }, [q]);

  useEffect(() => {
    const h = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setFocus(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  useEffect(() => {
    if (!busy) return undefined;
    const t0 = Date.now();
    const t = setInterval(() => setElapsed(Math.round((Date.now() - t0) / 1000)), 500);
    return () => clearInterval(t);
  }, [busy]);

  const exactCreator = useCallback((text) => {
    const k = canonKey(text);
    if (!k) return null;
    return list.find((c) => c.creator_key === k || canonKey(c.display_name) === k) || null;
  }, [list]);

  const groups = useMemo(() => {
    const v = q.trim();
    const vk = canonKey(v);
    const creatorItem = (c) => ({ kind: 'creator', c, label: <em>{c.display_name}</em>, h: `${(c.total || 0).toLocaleString('en-US')} videos` });
    if (!v) {
      const recent = list.filter((c) => c.status === 'active').slice()
        .sort((a, b) => String(b.last_upload || '').localeCompare(String(a.last_upload || ''))).slice(0, 4);
      const out = [{ label: 'Ask', items: STARTERS.map((s) => ({ kind: 'ask', text: s.t, label: s.t, h: s.h })) }];
      if (recent.length) out.push({ label: 'Recent creators', items: recent.map(creatorItem) });
      return out;
    }
    const out = [];
    const crs = vk ? list.filter((c) => c.creator_key.includes(vk) || canonKey(c.display_name).includes(vk))
      .sort((a, b) => (b.total || 0) - (a.total || 0)).slice(0, 5) : [];
    if (crs.length) out.push({ label: 'Creators', items: crs.map(creatorItem) });
    const lv = v.toLowerCase();
    const its = INTENTS.filter((i) => i.re.test(lv) && i.t.toLowerCase() !== lv).slice(0, 3)
      .map((i) => ({ kind: 'ask', text: i.t, label: i.t, h: i.h }));
    if (its.length) out.push({ label: 'Ask', items: its });
    out.push({ label: 'Or', items: [
      { kind: 'ask', text: v, label: <>Ask Timbo: <em>{v}</em></>, h: 'question' },
      ...(v.length >= 2 ? [{ kind: 'search', text: v, label: <>Search filenames for <em>“{v}”</em></>, h: 'keyword' }] : []),
    ] });
    return out;
  }, [q, list]);
  const flat = useMemo(() => groups.flatMap((g) => g.items), [groups]);

  const ask = useCallback(async (question) => {
    const text = String(question || '').trim();
    if (!text || busy) return;
    setFocus(false); setBusy(true); setErr(null); setElapsed(0); setLastQ(text);
    try {
      const r = await axios.post(`${API}/api/ask/scan`, { question: text }, { timeout: 90000 });
      if (!r.data || !r.data.ok) throw new Error((r.data && r.data.message) || 'No answer');
      setRes(r.data);
    } catch (e) {
      setRes(null); setErr(apiError(e));
    } finally {
      setBusy(false);
    }
  }, [busy]);

  const scope = (c) => { setFocus(false); setQ(''); setSel(-1); if (onScope) onScope(c); if (inputRef.current) inputRef.current.blur(); };

  const pick = (it) => {
    if (it.kind === 'creator') return scope(it.c);
    if (it.kind === 'search') { setFocus(false); setQ(''); if (onSearch) onSearch(it.text); return; }
    setQ(it.text); ask(it.text);
  };

  // Enter: highlighted suggestion > exact creator name (scopes the grid) > question.
  const submit = () => {
    if (sel >= 0 && focus && flat[sel]) return pick(flat[sel]);
    const c = exactCreator(q);
    if (c) return scope(c);
    ask(q);
  };

  const onKey = (e) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); setFocus(true); setSel((s) => Math.min(s + 1, flat.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setSel((s) => Math.max(s - 1, -1)); }
    else if (e.key === 'Enter') { e.preventDefault(); submit(); }
    else if (e.key === 'Escape') { setFocus(false); }
  };

  // A creator named in an answer row: scope the grid to them.
  const scopeKey = (key, label) => {
    const c = list.find((x) => x.creator_key === key) || (creators || []).find((x) => x.creator_key === key);
    scope(c || { creator_key: key, display_name: label });
  };

  const clear = () => { setRes(null); setErr(null); setQ(''); };
  const t = res && res.table;
  const keys = (t && t.creatorKeys) || [];
  const detailKey = res && res.creatorKey;
  let idx = -1;

  return (
    <div className="tb-ask">
      <div className={'tb-ask-unit' + (focus ? ' focus' : '')}>
      <div className="tb-ask-box" ref={boxRef}>
        <div className="tb-ask-bar">
          <span className={'tb-ask-brain' + (busy ? ' busy' : '')}><BrainIcon /></span>
          <input
            ref={inputRef}
            className="tb-ask-input"
            value={q}
            aria-label="Search a creator, or ask Timbo"
            autoComplete="off"
            spellCheck={false}
            onChange={(e) => { setQ(e.target.value); setSel(-1); setFocus(true); }}
            onFocus={() => setFocus(true)}
            onKeyDown={onKey}
          />
          {!q && <span className="tb-ask-ph">{PLACEHOLDERS[phIdx]}</span>}
          <button type="button" className="tb-ask-go" disabled={busy} onClick={() => (q.trim() ? submit() : inputRef.current && inputRef.current.focus())}>
            {busy ? '…' : 'Ask Timbo'}
          </button>
        </div>
        {focus && (
          <div className="tb-ask-sug">
            {groups.map((g) => (
              <div className="tb-ask-sg" key={g.label}>
                <div className="tb-ask-sl">{g.label}</div>
                {g.items.map((it) => { idx += 1; const i = idx; return (
                  <div key={i} className={'tb-ask-sr' + (i === sel ? ' sel' : '')} onMouseDown={(e) => { e.preventDefault(); pick(it); }}>
                    <span>{it.label}</span><span className="h">{it.h}</span>
                  </div>
                ); })}
              </div>
            ))}
            {!q && (
              <div className="tb-ask-sempty">
                Type a creator to show all their videos, or ask a question about uploads, features and the backlog.
                {' '}<span className="tb-ask-kbd">↑↓</span> to navigate, <span className="tb-ask-kbd">↵</span> to send.
              </div>
            )}
          </div>
        )}
      </div>

      {(busy || res || err) && (
        <div className="tb-ask-ans asks-ans tb-ask-clearfix">
          <div className="tb-ask-tools">
            {detailKey && !busy && <button type="button" className="tb-ask-ic txt" onClick={() => scopeKey(detailKey, res.sources && res.sources[0])}>Show their videos</button>}
            {(res || err) && !busy && <button type="button" className="tb-ask-ic" onClick={() => ask(lastQ)} title="Re-run this question on the latest index" aria-label="Re-run">⟳</button>}
            <button type="button" className="tb-ask-ic" onClick={clear} title="Clear the answer" aria-label="Clear">✕</button>
          </div>
          <div>
            {busy && (
              <div className="asks-wait">
                <span className="asks-dots"><i /><i /><i /></span>
                {elapsed < 4 ? 'Reading the question…' : 'Counting the Scan index…'}
              </div>
            )}
            {!busy && err && <div className="asks-err">{err}</div>}
            {!busy && !err && res && (
              <>
                {res.answer && res.answer.degraded && (
                  <div className="asks-degraded">Raw numbers only: the model call failed ({res.answer.degraded}{res.answer.degraded_message ? ' — ' + res.answer.degraded_message : ''}).</div>
                )}
                <p className="asks-lead"><RichText text={res.answer && res.answer.lead} /></p>
                {res.answer && res.answer.insights && res.answer.insights.length > 0 && (
                  <ul className="asks-list">{res.answer.insights.map((x, i) => <li key={i}><RichText text={x} /></li>)}</ul>
                )}
                {res.answer && res.answer.suggestions && res.answer.suggestions.length > 0 && (
                  <div className="asks-sugg">
                    <div className="l">Worth a look</div>
                    {res.answer.suggestions.map((x, i) => <div className="t" key={i}><RichText text={x} /></div>)}
                  </div>
                )}
                {res.kpis && res.kpis.length > 0 && (
                  <div className="asks-kpis">
                    {res.kpis.map((k, i) => (
                      <div className="asks-k" key={i}><div className="l">{k[0]}</div><div className="v" title={k[1]}>{k[1]}</div><div className="s" title={k[2]}>{k[2]}</div></div>
                    ))}
                  </div>
                )}
                {t && t.r && t.r.length > 0 && (
                  <>
                    {t.title && <div className="asks-tt">{t.title}</div>}
                    <div className="asks-scroll">
                      <table className="asks-tbl">
                        <thead><tr>{t.h.map((c, i) => <th key={i} className={i > 0 ? 'n' : ''}>{c}</th>)}</tr></thead>
                        <tbody>
                          {t.r.map((row, ri) => {
                            const key = keys[ri];
                            return (
                              <tr key={ri} className={key ? 'act' : ''} onClick={key ? () => scopeKey(key, row[0]) : undefined}
                                  title={key ? `Show all of ${row[0]}’s videos` : undefined}>
                                {row.map((c, ci) => (
                                  <td key={ci} className={ci > 0 ? 'n' : ''}>{ci === 0 && key ? <span className="asks-who">{c} →</span> : c}</td>
                                ))}
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                    {t.note && <div className="asks-note">{t.note}</div>}
                  </>
                )}
                {res.sources && res.sources.length > 0 && (
                  <div className="asks-src">Based on: {res.sources.map((s, i) => <span className="p" key={i}>{s}</span>)}</div>
                )}
              </>
            )}
          </div>
        </div>
      )}
      </div>
    </div>
  );
}
